import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { PackageCheck, Pencil, Share2 } from 'lucide-react';
import { IconTextButton, PrimaryButton } from '../ui';
import { LOST_FOUND_STATUS, buildLostFoundShareText } from './lostAndFoundData';
import {
  getLostFoundSiteId,
  isLikelyOpaqueId,
  lostAndFoundApi,
} from './lostAndFoundApi';
import CollectedByDetails from './CollectedByDetails';
import LostAndFoundItemSummary from './LostAndFoundItemSummary';

const LostAndFoundDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [item, setItem] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [shareMessage, setShareMessage] = useState('');
  const [isResolvingCollection, setIsResolvingCollection] = useState(false);

  const siteId = getLostFoundSiteId();

  const loadItem = useCallback(async () => {
    if (!siteId) {
      setError('Select a site on the Lost & Found list first.');
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    setError('');
    try {
      const data = await lostAndFoundApi.getById(id, siteId);
      if (data.collection) {
        const needsResolve =
          data.collection.block_id ||
          data.collection.flat_id ||
          isLikelyOpaqueId(data.collection.block) ||
          isLikelyOpaqueId(data.collection.flat);
        if (needsResolve) setIsResolvingCollection(true);
        data.collection = await lostAndFoundApi.enrichCollectionLabels(
          data.site_id || siteId,
          data.collection
        );
      }
      setItem(data);
    } catch (err) {
      setError(err.message || 'Failed to load item');
    } finally {
      setIsResolvingCollection(false);
      setIsLoading(false);
    }
  }, [id, siteId]);

  useEffect(() => {
    loadItem();
  }, [loadItem]);

  const handleShare = async () => {
    if (!item) return;
    const text = buildLostFoundShareText(item);
    const title = `Lost & Found: ${item.item_name}`;

    try {
      if (navigator.share) {
        await navigator.share({ title, text });
        setShareMessage('Shared successfully');
      } else if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        setShareMessage('Details copied to clipboard');
      } else {
        setShareMessage('Sharing is not supported in this browser');
      }
    } catch (err) {
      if (err?.name !== 'AbortError') {
        setShareMessage(err.message || 'Failed to share');
      }
    }
    setTimeout(() => setShareMessage(''), 3000);
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-16">
        <div className="w-8 h-8 border-4 border-[#6BABA0] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error || !item) {
    return (
      <div className="flex flex-col items-center gap-4 py-12">
        <p className="text-red-600">{error || 'Item not found'}</p>
      </div>
    );
  }

  const showCollectedBy =
    item.status === LOST_FOUND_STATUS.COLLECTED || Boolean(item.collection);

  return (
    <div className="w-full max-w-6xl flex flex-col gap-6">
      <div
        className={`grid grid-cols-1 gap-6 lg:gap-10 items-start ${
          showCollectedBy ? 'lg:grid-cols-[1fr_minmax(260px,340px)]' : ''
        }`}
      >
        <LostAndFoundItemSummary item={item} />

        {showCollectedBy && (
          <CollectedByDetails
            collection={item.collection}
            isResolvingLabels={isResolvingCollection}
          />
        )}
      </div>

      {shareMessage && (
        <p className="text-sm text-green-700 font-['Open_Sans']">{shareMessage}</p>
      )}

      <div className="flex flex-wrap items-center justify-end gap-3 pt-2">
        {item.status !== LOST_FOUND_STATUS.COLLECTED && (
          <IconTextButton
            type="button"
            icon={<Pencil className="w-5 h-5 text-white" aria-hidden />}
            text="Edit item"
            onClick={() => navigate(`/lost-and-found/${id}/edit`)}
            size="md"
            variant="default"
            className="!h-12 !min-w-[140px] !text-base !font-semibold !bg-blue-600 hover:!bg-blue-700"
          />
        )}
        <IconTextButton
          type="button"
          icon={<Share2 className="w-5 h-5 text-white" aria-hidden />}
          text="Share"
          onClick={handleShare}
          size="md"
          variant="default"
          className="!h-12 !min-w-[120px] !text-base !font-semibold !bg-[#6BABA0] hover:!bg-[#5a9a90]"
        />
        {item.status === LOST_FOUND_STATUS.FOUND && (
          <PrimaryButton
            type="button"
            onClick={() => navigate(`/lost-and-found/${id}/collect`)}
            className="!w-auto !h-12 !min-w-[180px] !text-base !font-semibold !font-['Open_Sans'] !bg-emerald-600 hover:!bg-emerald-700"
          >
            <span className="inline-flex items-center justify-center gap-2">
              <PackageCheck className="w-5 h-5 shrink-0" aria-hidden />
              Collect item
            </span>
          </PrimaryButton>
        )}
      </div>
    </div>
  );
};

export default LostAndFoundDetail;
